import type { BubbleDef, BoostZoneDef, ItemZoneDef, MapDefinition, PegDef, PopperDef, RotorDef, WallDef } from '../types';
import { getCustomMaps } from '../storage/customMaps';

const W = 560;
const H = 4300;
const GOAL = 4160;
const CX = W / 2;

// ─── Utility Builders ───────────────────────────────────────────────

function pegGrid(
  yStart: number,
  yEnd: number,
  rowSpacing: number,
  xEven: number,
  xOdd: number,
  xEnd: number,
  colSpacing: number,
  radius: number,
  restitution = 0.42,
): PegDef[] {
  const pegs: PegDef[] = [];
  let row = 0;
  for (let y = yStart; y <= yEnd; y += rowSpacing) {
    const xStart = row % 2 === 0 ? xEven : xOdd;
    for (let x = xStart; x <= xEnd; x += colSpacing) {
      pegs.push({ x, y, radius, restitution });
    }
    row++;
  }
  return pegs;
}

function rotor(x: number, y: number, speed: number, width = 128): RotorDef {
  return { x, y, width, height: 10, angularVelocity: speed, restitution: 0.05 };
}

function boost(y: number, forceY = 0.0045, forceX = 0): BoostZoneDef {
  return { x: CX, y, width: W - 90, height: 120, forceY, forceX };
}

// ─── Map Boundaries ────────────────────────────────────────────────
// Polyline waypoints define the ACTUAL left/right screen edges.
// Between each pair of waypoints we create a thick rectangle wall.

interface Waypoint { x: number; y: number }

function waypointsToWalls(waypoints: Waypoint[], thickness = 16): WallDef[] {
  const walls: WallDef[] = [];
  for (let i = 0; i < waypoints.length - 1; i++) {
    const a = waypoints[i];
    const b = waypoints[i + 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    const theta = Math.atan2(dy, dx);
    walls.push({
      x: (a.x + b.x) / 2,
      y: (a.y + b.y) / 2,
      width: len + 16,
      height: thickness,
      angle: theta,
      restitution: 0.18,
      friction: 0.018,
    });
  }
  return walls;
}

/** Place small circle caps at each interior waypoint junction to prevent marbles getting wedged */
function waypointsToCaps(waypoints: Waypoint[], radius = 20): PegDef[] {
  return waypoints.slice(1, -1).map((wp) => ({
    x: wp.x,
    y: wp.y,
    radius,
    restitution: 0.18,
  }));
}

function bub(id: string, x: number, y: number, radius = 13): BubbleDef {
  return { id, x, y, radius, restitution: 0.96 };
}

// ─── Helper: corridor-aware placement ──────────────────────────────

function getCorridorX(y: number, boundary: Waypoint[]): number {
  for (let i = 0; i < boundary.length - 1; i++) {
    if (y >= boundary[i].y && y <= boundary[i + 1].y) {
      const t = (y - boundary[i].y) / (boundary[i + 1].y - boundary[i].y);
      return boundary[i].x + t * (boundary[i + 1].x - boundary[i].x);
    }
  }
  return boundary[boundary.length - 1].x;
}

function itemInCorridor(id: string, kind: ItemZoneDef['kind'], y: number, leftWp: Waypoint[], rightWp: Waypoint[], xRatio = 0.5): ItemZoneDef {
  const leftX = getCorridorX(y, leftWp);
  const rightX = getCorridorX(y, rightWp);
  const margin = 35;
  const x = (leftX + margin) + (rightX - leftX - margin * 2) * xRatio;
  return { id, kind, x, y, radius: 30 };
}

function popInCorridor(id: string, y: number, leftWp: Waypoint[], rightWp: Waypoint[], xRatio = 0.5, force = 5.4): PopperDef {
  const leftX = getCorridorX(y, leftWp);
  const rightX = getCorridorX(y, rightWp);
  const margin = 35;
  const x = (leftX + margin) + (rightX - leftX - margin * 2) * xRatio;
  return { id, x, y, radius: 22, force };
}

// ════════════════════════════════════════════════════════════════════
// MAP 1: 핀볼 타워  —  Pegboard + rotating bars, classic pinball feel
// Wide corridors, many small pegs to bounce off, rotors for chaos
// ════════════════════════════════════════════════════════════════════

const pinballLeft: Waypoint[] = [
  { x: 5, y: -150 },
  { x: 5, y: 500 },
  { x: 100, y: 850 },
  { x: 100, y: 1200 },
  { x: 15, y: 1600 },
  { x: 15, y: 2000 },
  { x: 110, y: 2400 },
  { x: 110, y: 2700 },
  { x: 25, y: 3050 },
  { x: 25, y: 3400 },
  { x: 90, y: 3700 },
  { x: 247, y: 3920 },
  { x: 247, y: 4160 },
];

const pinballRight: Waypoint[] = [
  { x: 555, y: -150 },
  { x: 555, y: 600 },
  { x: 450, y: 950 },
  { x: 450, y: 1300 },
  { x: 545, y: 1700 },
  { x: 545, y: 2100 },
  { x: 440, y: 2500 },
  { x: 440, y: 2800 },
  { x: 530, y: 3100 },
  { x: 530, y: 3450 },
  { x: 460, y: 3700 },
  { x: 313, y: 3920 },
  { x: 313, y: 4160 },
];

const pinballWalls: WallDef[] = [
  ...waypointsToWalls(pinballLeft),
  ...waypointsToWalls(pinballRight),
];
const pinballPegs = [
  ...pegGrid(300, 520, 55, 80, 115, 480, 70, 6),
  ...pegGrid(850, 1200, 58, 130, 155, 440, 65, 6),  // xEven 120→130: left clearance 6px→16px
  ...pegGrid(1600, 2000, 55, 80, 115, 480, 70, 6),
  ...pegGrid(2400, 2700, 58, 140, 165, 420, 65, 6), // xEven 130→140, xEnd 430→420: 1px peg-wall gap fixed
  ...pegGrid(3050, 3400, 55, 90, 125, 470, 68, 6),
  ...pegGrid(3650, 3750, 50, 150, 185, 410, 60, 6),
  ...waypointsToCaps(pinballLeft.slice(0, -1)),
  ...waypointsToCaps(pinballRight.slice(0, -1)),
];
const pinballRotors = [
  rotor(CX, 780, 2.4, 130),
  rotor(200, 1450, -2.6, 100),
  rotor(360, 1450, 2.6, 100),
  rotor(CX, 2300, -2.5, 140),
  rotor(200, 2950, 2.2, 100),
  rotor(360, 2950, -2.2, 100),
  rotor(CX, 3860, -1.3, 100),
];
const pinballPoppers = [
  popInCorridor('pin-pop-1', 700, pinballLeft, pinballRight, 0.15),
  popInCorridor('pin-pop-2', 700, pinballLeft, pinballRight, 0.85),
  popInCorridor('pin-pop-2b', 1020, pinballLeft, pinballRight, 0.45, 6.1),
  popInCorridor('pin-pop-3', 1900, pinballLeft, pinballRight, 0.3),
  popInCorridor('pin-pop-4', 1900, pinballLeft, pinballRight, 0.7),
  popInCorridor('pin-pop-4b', 2600, pinballLeft, pinballRight, 0.5, 6.2),
  popInCorridor('pin-pop-5', 3500, pinballLeft, pinballRight, 0.5, 5.0),
  { id: 'pin-finish-pop', x: 280, y: 4120, radius: 12, force: 6.2 },
];
const pinballItems = [
  itemInCorridor('pin-boost-1', 'boost', 600, pinballLeft, pinballRight, 0.6),
  itemInCorridor('pin-shield-1', 'shield', 950, pinballLeft, pinballRight, 0.5),
  itemInCorridor('pin-slow-1', 'slow', 1400, pinballLeft, pinballRight, 0.4),
  itemInCorridor('pin-chaos-1', 'chaos', 2150, pinballLeft, pinballRight, 0.5),
  itemInCorridor('pin-swap-1', 'swap', 2800, pinballLeft, pinballRight, 0.65),
  itemInCorridor('pin-vortex-1', 'vortex', 3000, pinballLeft, pinballRight, 0.5),
  itemInCorridor('pin-boost-2', 'boost', 3200, pinballLeft, pinballRight, 0.3),
  itemInCorridor('pin-teleport-1', 'teleport', 3450, pinballLeft, pinballRight, 0.4),
  itemInCorridor('pin-slow-2', 'slow', 3600, pinballLeft, pinballRight, 0.7),
];
const pinballBubbles: BubbleDef[] = [
  bub('pin-bub-1', 280, 380),
  bub('pin-bub-1b', 210, 720),
  bub('pin-bub-1c', 350, 720),
  bub('pin-bub-2', 175, 1080, 12),
  bub('pin-bub-3', 370, 1820),
  bub('pin-bub-3b', 280, 2220, 15),
  bub('pin-bub-4', 225, 2640, 12),
  bub('pin-bub-5', 330, 3180),
  bub('pin-bub-6', 280, 3520, 15),
];

// ════════════════════════════════════════════════════════════════════
// MAP 2: 협곡 레이스  —  Narrow S-curves with boost zones, speed focus
// Tight alternating corridors force marbles to zigzag aggressively
// ════════════════════════════════════════════════════════════════════

const canyonLeft: Waypoint[] = [
  { x: 5, y: -150 },
  { x: 5, y: 350 },
  { x: 155, y: 700 },   // was 200 — widen 1st narrow: 150px→240px
  { x: 155, y: 1000 },
  { x: 20, y: 1350 },
  { x: 20, y: 1650 },
  { x: 155, y: 2000 },  // was 220 — widen 2nd narrow: 120px→245px (critical fix)
  { x: 155, y: 2300 },
  { x: 30, y: 2650 },
  { x: 30, y: 2950 },
  { x: 150, y: 3250 },  // was 180 — widen 3rd narrow: 200px→255px
  { x: 150, y: 3500 },
  { x: 80, y: 3750 },
  { x: 247, y: 3930 },
  { x: 247, y: 4160 },
];

const canyonRight: Waypoint[] = [
  { x: 555, y: -150 },
  { x: 555, y: 450 },
  { x: 395, y: 800 },   // was 350 — widen 1st narrow
  { x: 395, y: 1100 },
  { x: 540, y: 1450 },
  { x: 540, y: 1750 },
  { x: 400, y: 2100 },  // was 340 — widen 2nd narrow (critical fix)
  { x: 400, y: 2400 },
  { x: 530, y: 2750 },
  { x: 530, y: 3000 },
  { x: 405, y: 3300 },  // was 380 — widen 3rd narrow
  { x: 405, y: 3550 },
  { x: 470, y: 3750 },
  { x: 313, y: 3930 },
  { x: 313, y: 4160 },
];

const canyonWalls: WallDef[] = [
  ...waypointsToWalls(canyonLeft),
  ...waypointsToWalls(canyonRight),
];
const canyonPegs = [
  ...pegGrid(500, 700, 65, 140, 180, 420, 80, 5),
  ...pegGrid(1150, 1350, 65, 125, 150, 420, 80, 5), // xEven 110→125: left clearance -0px→+15px at y=1150
  ...pegGrid(1800, 2000, 65, 155, 180, 420, 80, 5), // xEven 140→155: left clearance -1px→+14px at y=1930
  ...pegGrid(2450, 2650, 65, 130, 150, 420, 80, 5), // xEven 110→130: left clearance -4px→+16px at y=2450
  ...pegGrid(3100, 3250, 65, 170, 190, 410, 80, 5), // xEven 150→170: left clearance -5px→+15px at y=3230
  ...pegGrid(3650, 3750, 50, 160, 200, 400, 60, 5),
  ...waypointsToCaps(canyonLeft.slice(0, -1)),
  ...waypointsToCaps(canyonRight.slice(0, -1)),
];
const canyonRotors = [
  rotor(CX, 480, 2.9, 100),
  rotor(CX, 1200, -3.1, 100),
  rotor(CX, 1900, 2.6, 110),
  rotor(CX, 2550, -2.8, 100),
  rotor(CX, 3150, 2.3, 100),
  rotor(CX, 3870, -1.3, 118),
];
const canyonPoppers = [
  popInCorridor('cn-pop-1', 600, canyonLeft, canyonRight, 0.2),
  popInCorridor('cn-pop-2', 1100, canyonLeft, canyonRight, 0.8),
  popInCorridor('cn-pop-3', 1800, canyonLeft, canyonRight, 0.3),
  popInCorridor('cn-pop-4', 2500, canyonLeft, canyonRight, 0.7),
  popInCorridor('cn-pop-5', 3600, canyonLeft, canyonRight, 0.5, 5.0),
  { id: 'cn-finish-pop', x: 280, y: 4120, radius: 12, force: 6.2 },
];
const canyonItems = [
  itemInCorridor('cn-boost-1', 'boost', 450, canyonLeft, canyonRight, 0.5),
  itemInCorridor('cn-shield-1', 'shield', 780, canyonLeft, canyonRight, 0.55),
  itemInCorridor('cn-chaos-1', 'chaos', 1050, canyonLeft, canyonRight, 0.6),
  itemInCorridor('cn-slow-1', 'slow', 1700, canyonLeft, canyonRight, 0.4),
  itemInCorridor('cn-teleport-1', 'teleport', 2150, canyonLeft, canyonRight, 0.5),
  itemInCorridor('cn-boost-2', 'boost', 2400, canyonLeft, canyonRight, 0.5),
  itemInCorridor('cn-chaos-2', 'chaos', 3100, canyonLeft, canyonRight, 0.3),
  itemInCorridor('cn-anchor-1', 'anchor', 3220, canyonLeft, canyonRight, 0.45),
  itemInCorridor('cn-swap-1', 'swap', 3350, canyonLeft, canyonRight, 0.6),
  itemInCorridor('cn-slow-2', 'slow', 3500, canyonLeft, canyonRight, 0.7),
];
const canyonBubbles: BubbleDef[] = [
  bub('cn-bub-1', 275, 620),
  bub('cn-bub-6', 190, 870, 12),
  bub('cn-bub-2', 185, 1480, 12),
  bub('cn-bub-7', 375, 1800, 12),
  bub('cn-bub-3', 375, 2200),
  bub('cn-bub-8', 185, 2560, 12),
  bub('cn-bub-4', 200, 2980, 12),
  bub('cn-bub-5', 360, 3620, 13),
];

// ════════════════════════════════════════════════════════════════════
// MAP 3: 역전 건틀릿  —  Rotor-heavy with wide corridors for drama
// Many spinning obstacles, wide lanes allow comebacks, items matter most
// ════════════════════════════════════════════════════════════════════

const gauntletLeft: Waypoint[] = [
  { x: 5, y: -150 },
  { x: 5, y: 600 },
  { x: 80, y: 900 },
  { x: 80, y: 1400 },
  { x: 15, y: 1700 },
  { x: 15, y: 2200 },
  { x: 100, y: 2500 },
  { x: 100, y: 3000 },
  { x: 20, y: 3300 },
  { x: 20, y: 3600 },
  { x: 100, y: 3800 },
  { x: 247, y: 3940 },
  { x: 247, y: 4160 },
];

const gauntletRight: Waypoint[] = [
  { x: 555, y: -150 },
  { x: 555, y: 700 },
  { x: 470, y: 1000 },
  { x: 470, y: 1500 },
  { x: 545, y: 1800 },
  { x: 545, y: 2300 },
  { x: 460, y: 2600 },
  { x: 460, y: 3100 },
  { x: 540, y: 3350 },
  { x: 540, y: 3650 },
  { x: 450, y: 3800 },
  { x: 313, y: 3940 },
  { x: 313, y: 4160 },
];

const gauntletWalls: WallDef[] = [
  ...waypointsToWalls(gauntletLeft),
  ...waypointsToWalls(gauntletRight),
];
const gauntletPegs = [
  ...pegGrid(400, 600, 60, 70, 110, 490, 72, 6),
  ...pegGrid(1100, 1400, 60, 110, 140, 450, 72, 6), // xEven 100→110, xEnd 460→450: -4px right overlap fixed
  ...pegGrid(1800, 2100, 60, 80, 120, 480, 72, 6),
  ...pegGrid(2550, 2850, 60, 128, 150, 435, 72, 6), // xEven 110→128, xEnd 450→435: left -4px / right 8px fixed
  ...pegGrid(3350, 3600, 55, 80, 120, 480, 68, 6),
  ...pegGrid(3700, 3800, 50, 160, 200, 400, 60, 5),
  ...waypointsToCaps(gauntletLeft.slice(0, -1)),
  ...waypointsToCaps(gauntletRight.slice(0, -1)),
];
const gauntletRotors = [
  // Dense rotor field — the signature of this map
  rotor(180, 750, 2.6, 120),
  rotor(380, 750, -2.6, 120),
  rotor(CX, 1050, -2.9, 150),
  rotor(180, 1550, 2.3, 120),
  rotor(380, 1550, -2.3, 120),
  rotor(CX, 1850, 2.7, 140),
  rotor(180, 2350, -2.7, 110),
  rotor(380, 2350, 2.7, 110),
  rotor(CX, 2650, -2.6, 160),
  rotor(180, 3150, 2.4, 110),
  rotor(380, 3150, -2.4, 110),
  rotor(CX, 3870, 1.2, 152),
];
const gauntletPoppers = [
  popInCorridor('gt-pop-1', 650, gauntletLeft, gauntletRight, 0.2),
  popInCorridor('gt-pop-2', 1300, gauntletLeft, gauntletRight, 0.8),
  popInCorridor('gt-pop-3', 2000, gauntletLeft, gauntletRight, 0.3),
  popInCorridor('gt-pop-4', 2800, gauntletLeft, gauntletRight, 0.7),
  popInCorridor('gt-pop-5', 3500, gauntletLeft, gauntletRight, 0.5, 5.0),
  { id: 'gt-finish-pop', x: 280, y: 4120, radius: 12, force: 6.2 },
];
const gauntletItems = [
  itemInCorridor('gt-boost-1', 'boost', 500, gauntletLeft, gauntletRight, 0.5),
  itemInCorridor('gt-shield-1', 'shield', 680, gauntletLeft, gauntletRight, 0.45),
  itemInCorridor('gt-slow-1', 'slow', 900, gauntletLeft, gauntletRight, 0.3),
  itemInCorridor('gt-chaos-1', 'chaos', 1250, gauntletLeft, gauntletRight, 0.7),
  itemInCorridor('gt-teleport-1', 'teleport', 1580, gauntletLeft, gauntletRight, 0.5),
  itemInCorridor('gt-boost-2', 'boost', 1700, gauntletLeft, gauntletRight, 0.4),
  itemInCorridor('gt-slow-2', 'slow', 2200, gauntletLeft, gauntletRight, 0.6),
  itemInCorridor('gt-chaos-2', 'chaos', 2700, gauntletLeft, gauntletRight, 0.5),
  itemInCorridor('gt-swap-1', 'swap', 3050, gauntletLeft, gauntletRight, 0.55),
  itemInCorridor('gt-vortex-1', 'vortex', 3180, gauntletLeft, gauntletRight, 0.48),
  itemInCorridor('gt-boost-3', 'boost', 3300, gauntletLeft, gauntletRight, 0.3),
];
const gauntletBubbles: BubbleDef[] = [
  bub('gt-bub-1', 280, 480),
  bub('gt-bub-2', 170, 1080, 12),
  bub('gt-bub-3', 380, 1620),
  bub('gt-bub-4', 185, 2180, 12),
  bub('gt-bub-5', 340, 2750),
  bub('gt-bub-6', 280, 3220),
];

// MAP 4: 버블 캐슬 - upward bounce focused, short but volatile
const bubbleCastleWalls = [
  ...waypointsToWalls(pinballLeft.map((p) => ({ x: p.y < 3800 && p.x < CX ? p.x + 18 : p.x, y: p.y }))),
  ...waypointsToWalls(pinballRight.map((p) => ({ x: p.y < 3800 && p.x > CX ? p.x - 18 : p.x, y: p.y }))),
];
const bubbleCastlePegs = [
  // BC walls are 18px narrower on each side vs pinball — peg bounds adjusted accordingly
  ...pegGrid(420, 900, 62, 130, 145, 430, 67, 5, 0.34),   // xEven 95→130, xOdd 130→145, xEnd 465→430
  ...pegGrid(1280, 1750, 62, 145, 160, 405, 67, 5, 0.34), // xEven 100→145, xOdd 135→160, xEnd 460→405
  ...pegGrid(2180, 2650, 62, 155, 185, 395, 67, 5, 0.34), // xEven 95→155, xOdd 130→185, xEnd 465→395
  ...pegGrid(3100, 3580, 58, 105, 140, 455, 64, 5, 0.34),
  ...waypointsToCaps(pinballLeft.slice(0, -1)),
  ...waypointsToCaps(pinballRight.slice(0, -1)),
];
const bubbleCastleRotors = [
  rotor(CX, 1120, 1.9, 150),
  rotor(190, 2040, -2.0, 105),
  rotor(370, 2040, 2.0, 105),
  rotor(CX, 3360, -1.8, 140),
  rotor(CX, 3870, 1.2, 75),
];
// Bubble positions manually verified to stay within BC corridor at each y
// BC walls = pinball ±18px; key corridor widths: y=560→39-537, y=1250→73-432, y=1940→73-527, y=2630→128-422, y=3230→43-512
const bubbleCastleBubbles: BubbleDef[] = [
  bub('bc-bub-0', 155, 560, 14),  bub('bc-bub-1', 250, 560, 15),
  bub('bc-bub-2', 330, 560, 16),  bub('bc-bub-3', 415, 560, 14),
  bub('bc-bub-4', 140, 1250, 14), bub('bc-bub-5', 220, 1250, 15),
  bub('bc-bub-6', 305, 1250, 14), bub('bc-bub-7', 385, 1250, 15), // was 448 — outside wall at 432
  bub('bc-bub-8', 155, 1940, 14), bub('bc-bub-9', 245, 1940, 16),
  bub('bc-bub-10', 325, 1940, 15), bub('bc-bub-11', 415, 1940, 14),
  bub('bc-bub-12', 162, 2630, 15), bub('bc-bub-13', 242, 2630, 14),
  bub('bc-bub-14', 322, 2630, 15), bub('bc-bub-15', 375, 2630, 16), // x 392→375: 2px→19px from BC wall (r×1.25=20)
  bub('bc-bub-16', 155, 3230, 14), bub('bc-bub-17', 415, 3230, 15),
];
const bubbleCastlePoppers = [
  popInCorridor('bc-pop-1', 850, pinballLeft, pinballRight, 0.25, 6.4),
  popInCorridor('bc-pop-2', 1550, pinballLeft, pinballRight, 0.75, 6.4),
  popInCorridor('bc-pop-3', 2450, pinballLeft, pinballRight, 0.35, 6.4),
  popInCorridor('bc-pop-4', 3300, pinballLeft, pinballRight, 0.65, 6.4),
  { id: 'bc-finish-pop', x: 280, y: 4120, radius: 12, force: 6.2 },
];
const bubbleCastleItems = [
  itemInCorridor('bc-boost-1', 'boost', 390, pinballLeft, pinballRight, 0.5),
  itemInCorridor('bc-shield-1', 'shield', 760, pinballLeft, pinballRight, 0.5),
  itemInCorridor('bc-chaos-1', 'chaos', 1200, pinballLeft, pinballRight, 0.4),
  itemInCorridor('bc-vortex-1', 'vortex', 1780, pinballLeft, pinballRight, 0.5),
  itemInCorridor('bc-slow-1', 'slow', 2350, pinballLeft, pinballRight, 0.6),
  itemInCorridor('bc-teleport-1', 'teleport', 2860, pinballLeft, pinballRight, 0.5),
  itemInCorridor('bc-swap-1', 'swap', 3200, pinballLeft, pinballRight, 0.45),
  itemInCorridor('bc-anchor-1', 'anchor', 3520, pinballLeft, pinballRight, 0.55),
];

// MAP 5: 슬라럼 스프린트 - fewer pegs, fast alternating gates
const slalomLeft: Waypoint[] = [
  { x: 20, y: -150 }, { x: 20, y: 500 },
  { x: 170, y: 950 },  // was 230 — widen narrows: 100px→220px
  { x: 55, y: 1450 },  // was 80 — push wide section further left for bigger swing
  { x: 170, y: 1950 }, // was 230
  { x: 55, y: 2450 },  // was 80
  { x: 170, y: 3000 }, // was 230
  { x: 100, y: 3560 },
  { x: 247, y: 3940 }, { x: 247, y: 4160 },
];
const slalomRight: Waypoint[] = [
  { x: 540, y: -150 }, { x: 540, y: 560 },
  { x: 390, y: 1010 }, // was 330 — widen narrows (paired with left fix)
  { x: 505, y: 1510 }, // was 480 — push wide section further right
  { x: 390, y: 2010 }, // was 330
  { x: 505, y: 2510 }, // was 480
  { x: 390, y: 3040 }, // was 330
  { x: 460, y: 3560 },
  { x: 313, y: 3940 }, { x: 313, y: 4160 },
];
const slalomWalls = [...waypointsToWalls(slalomLeft), ...waypointsToWalls(slalomRight)];
const slalomPegs = [
  ...pegGrid(760, 940, 72, 185, 235, 375, 90, 5, 0.38), // restitution 0.28→0.38, x range inset from new walls
  ...pegGrid(1720, 1910, 72, 185, 235, 375, 90, 5, 0.38),
  ...pegGrid(2820, 2990, 72, 185, 235, 375, 90, 5, 0.38),
  ...waypointsToCaps(slalomLeft.slice(0, -1)),
  ...waypointsToCaps(slalomRight.slice(0, -1)),
];
const slalomRotors = [
  rotor(CX, 680, 2.6, 100),
  rotor(CX, 1600, -2.6, 100),
  rotor(CX, 2600, 2.6, 100),
  rotor(CX, 3720, 1.4, 175),
];
const slalomPoppers = [
  popInCorridor('sl-pop-1a', 1180, slalomLeft, slalomRight, 0.28, 5.8), // was 0.5 — split to sides
  popInCorridor('sl-pop-1b', 1180, slalomLeft, slalomRight, 0.72, 5.8),
  popInCorridor('sl-pop-2', 2180, slalomLeft, slalomRight, 0.5, 5.8),
  popInCorridor('sl-pop-3a', 3340, slalomLeft, slalomRight, 0.3, 5.8),
  popInCorridor('sl-pop-3b', 3340, slalomLeft, slalomRight, 0.7, 5.8),
  { id: 'sl-finish-pop', x: 280, y: 4120, radius: 12, force: 6.2 },
];
const slalomItems = [
  itemInCorridor('sl-boost-0', 'boost', 400, slalomLeft, slalomRight, 0.5),
  itemInCorridor('sl-boost-1', 'boost', 900, slalomLeft, slalomRight, 0.5),
  itemInCorridor('sl-shield-1', 'shield', 1260, slalomLeft, slalomRight, 0.35),
  itemInCorridor('sl-slow-1', 'slow', 1860, slalomLeft, slalomRight, 0.5),
  itemInCorridor('sl-chaos-1', 'chaos', 2200, slalomLeft, slalomRight, 0.65),
  itemInCorridor('sl-swap-1', 'swap', 2920, slalomLeft, slalomRight, 0.5),
  itemInCorridor('sl-teleport-1', 'teleport', 3200, slalomLeft, slalomRight, 0.4),
  itemInCorridor('sl-vortex-1', 'vortex', 3500, slalomLeft, slalomRight, 0.5),
];
const slalomBubbles = [
  bub('sl-bub-1a', 225, 1320, 13), // was 280 center — off-center pair avoids blocking the only path
  bub('sl-bub-1b', 335, 1320, 13),
  bub('sl-bub-2a', 225, 2320, 13),
  bub('sl-bub-2b', 335, 2320, 13),
  bub('sl-bub-3', 280, 3460, 15),
];

// ─── Export ────────────────────────────────────────────────────────

export const MAPS: MapDefinition[] = [
  {
    id: 'pinball-tower',
    name: '핀볼 타워',
    description: '수많은 페그와 회전 막대가 만드는 클래식 핀볼! 어디로 튈지 모르는 짜릿한 레이스',
    difficulty: 3,
    thumbnail: '🎯',
    worldWidth: W,
    worldHeight: H,
    goalY: GOAL,
    startZoneY: 35,
    walls: pinballWalls,
    pegs: pinballPegs,
    rotors: pinballRotors,
    boostZones: [boost(1040), boost(2260), boost(3820, 0.0065)],
    itemZones: pinballItems,
    poppers: pinballPoppers,
    bubbles: pinballBubbles,
    bgmTrack: 'william_tell',
    bgStyle: 'arcade',
  },
  {
    id: 'canyon-race',
    name: '협곡 레이스',
    description: '좁고 꼬불꼬불한 S자 협곡을 질주! 벽을 타는 테크닉이 승부를 가릅니다',
    difficulty: 4,
    thumbnail: '🏔️',
    worldWidth: W,
    worldHeight: H,
    goalY: GOAL,
    startZoneY: 35,
    walls: canyonWalls,
    pegs: canyonPegs,
    rotors: canyonRotors,
    boostZones: [boost(800), boost(1500), boost(2200), boost(2900), boost(3600, 0.006)],
    itemZones: canyonItems,
    poppers: canyonPoppers,
    bubbles: canyonBubbles,
    bgmTrack: 'hero_quest',
    bgStyle: 'desert',
  },
  {
    id: 'reverse-gauntlet',
    name: '역전 건틀릿',
    description: '12개의 회전 막대가 끊임없이 순위를 뒤집는 극한 코스! 끝까지 모른다',
    difficulty: 5,
    thumbnail: '🌀',
    worldWidth: W,
    worldHeight: H,
    goalY: GOAL,
    startZoneY: 35,
    walls: gauntletWalls,
    pegs: gauntletPegs,
    rotors: gauntletRotors,
    boostZones: [boost(1240), boost(2520), boost(3820, 0.0065)],
    itemZones: gauntletItems,
    poppers: gauntletPoppers,
    bubbles: gauntletBubbles,
    bgmTrack: 'rock_4bit',
    bgStyle: 'dungeon',
  },
  {
    id: 'bubble-castle',
    name: '버블 캐슬',
    description: '보라색 버블이 공을 위로 튕겨 보내는 반동 중심 맵입니다',
    difficulty: 4,
    thumbnail: '🫧',
    worldWidth: W,
    worldHeight: H,
    goalY: GOAL,
    startZoneY: 35,
    walls: bubbleCastleWalls,
    pegs: bubbleCastlePegs,
    rotors: bubbleCastleRotors,
    boostZones: [boost(1200, 0.004), boost(2450, 0.004), boost(3600, 0.005)],
    itemZones: bubbleCastleItems,
    poppers: bubbleCastlePoppers,
    bubbles: bubbleCastleBubbles,
    bgmTrack: 'heroquest_pop',
    bgStyle: 'pastel',
  },
  {
    id: 'slalom-sprint',
    name: '슬라럼 스프린트',
    description: '핀은 적고 루트가 빠르게 좌우 전환되는 속도형 협곡 맵입니다',
    difficulty: 4,
    thumbnail: '🏁',
    worldWidth: W,
    worldHeight: H,
    goalY: GOAL,
    startZoneY: 35,
    walls: slalomWalls,
    pegs: slalomPegs,
    rotors: slalomRotors,
    boostZones: [boost(800, 0.005), boost(1750, 0.005), boost(2750, 0.005), boost(3600, 0.0055)],
    itemZones: slalomItems,
    poppers: slalomPoppers,
    bubbles: slalomBubbles,
    bgmTrack: 'enthusiast',
    bgStyle: 'frost',
  },
];

export function getAvailableMaps(): MapDefinition[] {
  return [...MAPS, ...getCustomMaps()];
}

export function getMapById(id: string): MapDefinition {
  return getAvailableMaps().find((m) => m.id === id) ?? MAPS[0];
}
